import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

// Money helpers — the ₹ AMOUNT is always the source of truth; the % is derived.
export const pctToAmt = (pct, base) => Math.round((Number(base) || 0) * (Number(pct) || 0) / 100);
export const amtToPct = (amt, base) => {
  const b = Number(base) || 0;
  if (b <= 0) return 0;
  const p = ((Number(amt) || 0) / b) * 100;
  return Math.round(p * 10) / 10; // one decimal for display
};
export const clampNum = (v, lo, hi) => {
  if (isNaN(v)) return lo;
  return Math.max(lo, Math.min(hi, v));
};

/**
 * Two linked inputs: "amount (₹)" and "percentage (%)" of `base`.
 * Whichever the admin types in wins; the OTHER updates automatically.
 * The parent stores only the rupee `amount`; `onChange(newAmount)` fires with a
 * clamped integer rupee value. Percentage is derived (rounded) for display.
 */
export const LinkedAmountPercent = ({
  base,
  amount,
  onChange,
  amountLabel = 'Amount (₹)',
  percentLabel = 'Percentage (%)',
  maxPercent = 100,
  minAmount = 0,
  testIdPrefix = 'linked',
  showBalance = false,
  balanceLabel = 'due at venue',
  invalidHint,
}) => {
  const b = Number(base) || 0;
  const amt = Number(amount) || 0;
  const pct = amtToPct(amt, b);
  const belowMin = minAmount > 0 && amt > 0 && amt < minAmount;
  const overBase = amt > b;
  const invalid = overBase || belowMin;

  const setFromAmount = (raw) => {
    const next = clampNum(parseFloat(raw) || 0, 0, b);
    onChange(next);
  };
  const setFromPercent = (raw) => {
    const p = clampNum(parseFloat(raw) || 0, 0, maxPercent);
    onChange(pctToAmt(p, b));
  };

  return (
    <div className="space-y-1.5">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">{amountLabel}</Label>
          <Input
            type="number"
            min="0"
            max={b}
            value={amt === 0 ? '' : amt}
            onChange={(e) => setFromAmount(e.target.value)}
            placeholder="0"
            className="text-sm"
            data-testid={`${testIdPrefix}-amount`}
          />
        </div>
        <div>
          <Label className="text-xs">{percentLabel}</Label>
          <Input
            type="number"
            step="any"
            min="0"
            max={maxPercent}
            value={pct === 0 ? '' : pct}
            onChange={(e) => setFromPercent(e.target.value)}
            placeholder="0"
            className="text-sm"
            data-testid={`${testIdPrefix}-percent`}
          />
        </div>
      </div>
      {showBalance && amt > 0 && !invalid && (
        <p className="text-xs text-gray-600" data-testid={`${testIdPrefix}-balance`}>
          ₹{amt} now · <span className="font-medium">₹{Math.max(0, b - amt)} {balanceLabel}</span>
        </p>
      )}
      {invalid && (
        <p className="text-xs text-red-600 font-medium" data-testid={`${testIdPrefix}-invalid`}>
          {overBase
            ? `Cannot exceed ₹${b}.`
            : (invalidHint || `Minimum is ₹${minAmount}.`)}
        </p>
      )}
    </div>
  );
};

export default LinkedAmountPercent;
